import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Shield, RefreshCw, CheckCircle, ChevronDown } from 'lucide-react';
import { usePageMeta } from '@/hooks/usePageMeta';

const LandingPage = () => {
  const navigate = useNavigate();
  usePageMeta({
    title: 'Claritas - Secure Catholic Giving Platform',
    description:
      'Support your nonprofit through secure, automated giving. Connect your bank account and automatically round up everyday purchases to donate to your Catholic nonprofit community.',
  });

  const scrollToFeatures = () => {
    const featuresSection = document.getElementById('features');
    if (featuresSection) {
      featuresSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      {/* Hero Section */}
      <section
        className="relative w-full h-screen -mt-16 bg-no-repeat flex items-center justify-center [background-size:cover] md:[background-size:130%]"
        style={{
          backgroundImage: 'url(/DSC_0101.JPG)',
          backgroundPosition: 'calc(50% + 5px) calc(50% - 35px)',
        }}
      >
        {/* Subtle dark center tint for text legibility */}
        <div
          className="absolute inset-0"
          style={{ background: 'radial-gradient(ellipse at center, rgba(11,17,26,0.45) 0%, rgba(11,17,26,0) 60%)' }}
        />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="text-center drop-shadow-lg">
            <p className="text-xs font-extrabold tracking-widest uppercase text-gold mb-6">
              Secure Catholic Giving
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-display text-white mb-4">
              Support Your Nonprofit
              <br />
              <span className="text-gold">Through Secure Giving</span>
            </h1>
            <div className="w-12 h-px bg-gold mx-auto mb-6" />
            <p className="text-lg text-white/80 max-w-2xl mx-auto mb-8">
              Connect your bank account and automatically round up your purchases to donate to your nonprofit.
              Secure, transparent, and meaningful contributions to your faith community.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                onClick={() => navigate('/parishes')}
                className="bg-gold text-black font-semibold hover:bg-gold/90 shadow-gold"
              >
                Find Your Nonprofit
              </Button>
              <Button
                size="lg"
                onClick={scrollToFeatures}
                className="border-2 border-white/70 text-white bg-transparent hover:bg-white/10 hover:border-white"
              >
                Learn More
              </Button>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
          <button
            onClick={scrollToFeatures}
            className="animate-bounce cursor-pointer text-white/60 hover:text-white transition-colors duration-150 focus:outline-none focus:ring-1 focus:ring-white/30 rounded p-2 flex items-center justify-center"
            aria-label="Scroll to features"
          >
            <ChevronDown className="w-6 h-6" />
          </button>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="bg-background-light py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-xs font-extrabold tracking-widest uppercase text-gold mb-4">
              The Platform
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold font-display text-charcoal mb-4">
              Secure, Transparent, Purposeful
            </h2>
            <p className="text-base text-charcoal-muted max-w-2xl mx-auto">
              Enterprise-grade security for your nonprofit community
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white p-6 border border-surface-border rounded shadow-card hover:border-gold/30 hover:shadow-subtle transition-all duration-150">
              <div className="w-10 h-10 border border-gold/30 rounded flex items-center justify-center mb-5">
                <Shield className="w-5 h-5 text-gold" />
              </div>
              <h3 className="text-lg font-bold font-display text-charcoal mb-2">
                Secure Bank Linking
              </h3>
              <p className="text-sm text-charcoal-muted">
                Connect your bank account securely through Stripe. Your financial data is encrypted and protected at every step.
              </p>
            </div>

            <div className="bg-white p-6 border border-surface-border rounded shadow-card hover:border-gold/30 hover:shadow-subtle transition-all duration-150">
              <div className="w-10 h-10 border border-gold/30 rounded flex items-center justify-center mb-5">
                <RefreshCw className="w-5 h-5 text-gold" />
              </div>
              <h3 className="text-lg font-bold font-display text-charcoal mb-2">
                Round-Up Donations
              </h3>
              <p className="text-sm text-charcoal-muted">
                Automatically round up your purchases and donate the difference to your nonprofit. Every transaction contributes.
              </p>
            </div>

            <div className="bg-white p-6 border border-surface-border rounded shadow-card hover:border-gold/30 hover:shadow-subtle transition-all duration-150">
              <div className="w-10 h-10 border border-gold/30 rounded flex items-center justify-center mb-5">
                <CheckCircle className="w-5 h-5 text-gold" />
              </div>
              <h3 className="text-lg font-bold font-display text-charcoal mb-2">
                One-Time Donations
              </h3>
              <p className="text-sm text-charcoal-muted">
                Make direct donations to your nonprofit anytime. Secure payments processed through Stripe.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-xs font-extrabold tracking-widest uppercase text-gold mb-4">
              The Process
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold font-display text-charcoal mb-4">
              How It Works
            </h2>
            <p className="text-base text-charcoal-muted max-w-2xl mx-auto">
              A straightforward process for secure giving
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-14 h-14 border-2 border-gold text-gold flex items-center justify-center text-xl font-bold font-display mx-auto mb-5">
                1
              </div>
              <h3 className="text-lg font-bold font-display text-charcoal mb-2">
                Create Your Account
              </h3>
              <p className="text-sm text-charcoal-muted">
                Register and select your nonprofit from our directory
              </p>
            </div>

            <div className="text-center">
              <div className="w-14 h-14 border-2 border-gold text-gold flex items-center justify-center text-xl font-bold font-display mx-auto mb-5">
                2
              </div>
              <h3 className="text-lg font-bold font-display text-charcoal mb-2">
                Link Your Bank
              </h3>
              <p className="text-sm text-charcoal-muted">
                Securely connect your bank account
              </p>
            </div>

            <div className="text-center">
              <div className="w-14 h-14 border-2 border-gold text-gold flex items-center justify-center text-xl font-bold font-display mx-auto mb-5">
                3
              </div>
              <h3 className="text-lg font-bold font-display text-charcoal mb-2">
                Start Giving
              </h3>
              <p className="text-sm text-charcoal-muted">
                Enable round-ups or make one-time donations
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-background-light py-20 border-t border-surface-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-extrabold tracking-widest uppercase text-gold mb-4">
            Join Today
          </p>
          <div className="w-8 h-px bg-gold mx-auto mb-6" />
          <h2 className="text-3xl sm:text-4xl font-bold font-display text-charcoal mb-4">
            Begin Your Contribution
          </h2>
          <p className="text-base text-charcoal-muted mb-8 max-w-xl mx-auto">
            Join your nonprofit community in supporting meaningful causes through secure, automated giving
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button size="lg" onClick={() => navigate('/sign-in')} className="bg-gold text-black font-semibold hover:bg-gold/90 shadow-gold">
              Get Started
            </Button>
            <Button variant="ghost" size="lg" onClick={() => navigate('/sign-in')}>
              Sign In
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-surface-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex flex-col md:flex-row justify-between items-start gap-8">
            <div>
              <div className="text-xl font-bold font-display text-charcoal mb-2">
                Claritas
              </div>
              <p className="text-sm text-charcoal-muted max-w-xs">
                Supporting Catholic nonprofits through secure, automated giving.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-10">
              <div>
                <p className="text-xs font-extrabold tracking-widest uppercase text-gold mb-3">
                  Product
                </p>
                <div className="flex flex-col gap-2">
                  <Link to="/how-it-works" className="text-sm text-charcoal-muted hover:text-charcoal transition-colors duration-150">
                    How It Works
                  </Link>
                  <Link to="/for-donors" className="text-sm text-charcoal-muted hover:text-charcoal transition-colors duration-150">
                    For Donors
                  </Link>
                  <Link to="/for-parishes" className="text-sm text-charcoal-muted hover:text-charcoal transition-colors duration-150">
                    For Parishes
                  </Link>
                </div>
              </div>

              <div>
                <p className="text-xs font-extrabold tracking-widest uppercase text-gold mb-3">
                  Company
                </p>
                <div className="flex flex-col gap-2">
                  <Link to="/security" className="text-sm text-charcoal-muted hover:text-charcoal transition-colors duration-150">
                    Security
                  </Link>
                  <Link to="/pricing" className="text-sm text-charcoal-muted hover:text-charcoal transition-colors duration-150">
                    Pricing
                  </Link>
                  <Link to="/privacy" className="text-sm text-charcoal-muted hover:text-charcoal transition-colors duration-150">
                    Privacy Policy
                  </Link>
                  <Link to="/terms" className="text-sm text-charcoal-muted hover:text-charcoal transition-colors duration-150">
                    Terms of Service
                  </Link>
                  <Link to="/support" className="text-sm text-charcoal-muted hover:text-charcoal transition-colors duration-150">
                    Support
                  </Link>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 pt-8 border-t border-surface-border">
            <p className="text-xs text-charcoal-faint">
              © {new Date().getFullYear()} Claritas. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </>
  );
};

export default LandingPage;
